import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthService } from 'src/app/authentication/auth.service';
import { UsersService } from '../user';
import { HttpContext } from '@angular/common/http';
import { OrganizationService } from '../organization';
import { getTimeZones } from '@vvo/tzdb';
import { format } from 'date-fns';

@Injectable({
  providedIn: 'root',
})

export class TimeZoneService {

  private readonly authService = inject(AuthService);
  private readonly usersService = inject(UsersService);
  private readonly organizationService = inject(OrganizationService);
  
  private browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  private userTimeZone = signal<string | null>(null);
  private orgTimeZone = signal<string | null>(null);
  
  public readonly timeZones = getTimeZones().map(tz => ({
    name: tz.name,
    label: tz.rawFormat,
    offset: tz.currentTimeOffsetInMinutes
  }));
  
  public readonly displayTimeZone = computed(() => 
    this.userTimeZone() || this.orgTimeZone() || this.browserTimeZone
  );

  public readonly displayTimeOffset = computed(() => {
    const tz = this.timeZones.find(z => z.name === this.displayTimeZone());
    return tz?.offset ?? 0;
  });
  
  public isTimeZoneLoaded = signal<boolean>(false);

  constructor(){
    this.fetchUserTimeZone();

    effect(() => {
      if (this.userTimeZone() === null) {
        this.fetchOrgTimeZone();
      }
    });
  }

  public getUserTimeZone():string{
    return this.userTimeZone();
  }

  public setUserTimeZone(tz: string | null):void{
    this.userTimeZone.set(tz === 'Etc/UTC' ? null : tz);
  }

  public setOrgTimeZone(tz: string | null):void{
    this.orgTimeZone.set(tz === 'Etc/UTC' ? null : tz);
  }

  public async fetchUserTimeZone(){
    const observe:any = 'body';
    const options:any = { httpHeaderAccept: 'text/plain', context: HttpContext };
    try{
      const data = await firstValueFrom(
        this.usersService.apiUsersGetUserTimeZoneOrganizationIdUserIdGet(
          this.authService.organizationId,
          this.authService.user.profile.UserID,
          observe,
          false,
          options
        )
      );
      this.setUserTimeZone(data === 'Etc/UTC' ? null : data);
      if(data) this.isTimeZoneLoaded.set(true);
    }catch(error){
      this.setUserTimeZone(null);
    }
  }

  public async fetchOrgTimeZone(){
    const observe:any = 'body';
    const options:any = { httpHeaderAccept: 'text/plain', context: HttpContext };
    try{
      const data = await firstValueFrom(
        this.organizationService.apiOrganizationGetOrganizationTimeZoneOrganizationIdGet(
          this.authService.organizationId,
          observe,
          false,
          options
        )
      );
      this.setOrgTimeZone(data === 'Etc/UTC' ? null : data);
      this.isTimeZoneLoaded.set(true);
    } catch (error) {
      this.setOrgTimeZone(null);
    }
  }

  private getUTC(value): Date {
    const date = (value instanceof Date) ? value : new Date(value);
    return new Date(date.getTime() + this.displayTimeOffset() * 60000);
  }
  
  public getDate(date: string | number | Date):string{
    return date ? format(this.getUTC(date), 'yyyy-MM-dd') : 'N/A';
  }
  
  public getTimestamp(date: string | number | Date):string{
    return date ? format(this.getUTC(date), 'yyyy-MM-dd HH:mm:ss') : 'N/A';
  }

  public getTimetampWithMinDate(date: string | number | Date):string{
    const utcDate = this.getUTC(date);
    return utcDate >= new Date('2000-01-01T00:00:00Z') ? format(utcDate, 'yyyy-MM-dd HH:mm:ss') : 'N/A';
  }
  
}
